import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import ControlHorariLayout from "./ControlHorariLayout";
import { chFetch, ChSessioCaducadaError } from "./chAuth";
import "./controlhorari.css";

function formatDataCurta(dataISO) {
  if (!dataISO) return "--";
  const [any, mes, dia] = dataISO.split("-");
  return `${dia}/${mes}/${any}`;
}

function formatMinuts(minuts) {
  if (minuts === null || minuts === undefined) return "--";
  const signe = minuts < 0 ? "-" : "";
  const abs = Math.abs(minuts);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${signe}${h} h ${String(m).padStart(2, "0")} min`;
}

function primerDiaMesActual() {
  const ara = new Date();
  return `${ara.getFullYear()}-${String(ara.getMonth() + 1).padStart(2, "0")}-01`;
}

function avuiISO() {
  const ara = new Date();
  return `${ara.getFullYear()}-${String(ara.getMonth() + 1).padStart(2, "0")}-${String(ara.getDate()).padStart(2, "0")}`;
}

// Pantalla "Registres" del portal mòbil (CURRENT_TASK_CALENDARI_I_COMPUTS.md
// §8): còmput d'hores (objectiu/treballades/pendents) per a l'interval de
// dates seleccionat, amb desglossament per dia. No duplica "Històric"
// (events detallats): aquí només es mostren totals i saldo per dia.
function ControlHorariRegistres() {
  const [dataInici, setDataInici] = useState(primerDiaMesActual());
  const [dataFinal, setDataFinal] = useState(avuiISO());
  const [comput, setComput] = useState(null);
  const [vigencia, setVigencia] = useState(null);
  const [carregant, setCarregant] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const carregar = useCallback(async () => {
    setCarregant(true);
    try {
      const resposta = await chFetch(`/ControlHorariRegistres.php?dataInici=${dataInici}&dataFinal=${dataFinal}`);
      const dades = await resposta.json();
      if (!dades.success) {
        setError(dades.message || "No s'han pogut carregar els Registres.");
        setComput(null);
        setVigencia(null);
        return;
      }
      setComput(dades.comput);
      setVigencia(dades.vigencia || null);
      setError("");
    } catch (err) {
      if (err instanceof ChSessioCaducadaError) {
        navigate("/Control_Horari/login", { replace: true });
        return;
      }
      setError("No s'ha pogut connectar amb el servidor.");
    } finally {
      setCarregant(false);
    }
  }, [dataInici, dataFinal, navigate]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const diesAmbActivitat = (comput?.dies || []).filter(
    (d) => d.previstMinuts > 0 || d.treballatMinuts > 0
  );

  return (
    <ControlHorariLayout titol="Registres" ajuda="registres">
      <div className="ch-registres">
        <div className="ch-registres-periode">
          <label>
            Des de
            <input type="date" value={dataInici} max={dataFinal} onChange={(e) => setDataInici(e.target.value)} />
          </label>
          <label>
            Fins a
            <input type="date" value={dataFinal} min={dataInici} onChange={(e) => setDataFinal(e.target.value)} />
          </label>
        </div>

        {error && (
          <div className="ch-avis-error">
            <FontAwesomeIcon icon={faCircleExclamation} /> {error}
          </div>
        )}

        {!error && vigencia?.totalmentForaVigencia && (
          <div className="ch-avis-error">
            <FontAwesomeIcon icon={faCircleExclamation} /> Període fora de la relació laboral: no hi ha hores previstes en aquestes dates.
          </div>
        )}
        {!error && vigencia?.parcialmentForaVigencia && (
          <div className="ch-avis-error">
            <FontAwesomeIcon icon={faCircleExclamation} /> Part d'aquest període ({vigencia.diesForaVigencia} de {vigencia.diesTotal} dies) és fora de la relació laboral: no es compten hores previstes en aquests dies.
          </div>
        )}

        {carregant ? (
          <div className="ch-carregant">Carregant...</div>
        ) : comput ? (
          <>
            <div className="ch-targeta">
              <div className="ch-targeta-capcalera">
                <span>Totals del període</span>
              </div>
              <div className="ch-registres-totals">
                <div className="ch-registres-total">
                  <span className="ch-registres-total-valor">{formatMinuts(comput.horesObjectiuMinuts)}</span>
                  <span className="ch-registres-total-etiqueta">Objectiu</span>
                </div>
                <div className="ch-registres-total">
                  <span className="ch-registres-total-valor">{formatMinuts(comput.horesTreballadesMinuts)}</span>
                  <span className="ch-registres-total-etiqueta">Treballat</span>
                </div>
                {comput.horesExcedentsMinuts > 0 ? (
                  <div className="ch-registres-total">
                    <span className="ch-registres-total-valor ch-registres-excedent">+{formatMinuts(comput.horesExcedentsMinuts)}</span>
                    <span className="ch-registres-total-etiqueta">Excedent</span>
                  </div>
                ) : (
                  <div className="ch-registres-total">
                    <span className="ch-registres-total-valor ch-registres-pendent">{formatMinuts(comput.horesPendentsMinuts)}</span>
                    <span className="ch-registres-total-etiqueta">Pendent</span>
                  </div>
                )}
              </div>
            </div>

            <div className="ch-targeta">
              <div className="ch-targeta-capcalera">
                <span>Desglossament per dia</span>
              </div>
              {diesAmbActivitat.length === 0 ? (
                <p className="ch-text-suau">No hi ha cap dia amb activitat en aquest període.</p>
              ) : (
                diesAmbActivitat.map((d) => (
                  <div className="ch-registres-dia" key={d.Data}>
                    <div className="ch-registres-dia-data">{formatDataCurta(d.Data)}</div>
                    <div className="ch-registres-dia-linies">
                      <span>Treballat: {formatMinuts(d.treballatMinuts)}</span>
                      <span>Previst: {formatMinuts(d.previstMinuts)}</span>
                      <span className={d.saldoMinuts < 0 ? "ch-registres-saldo-negatiu" : "ch-registres-saldo-positiu"}>
                        Saldo: {d.saldoMinuts >= 0 ? "+" : ""}{formatMinuts(d.saldoMinuts)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : null}
      </div>
    </ControlHorariLayout>
  );
}

export default ControlHorariRegistres;
